import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const errorContainer = payload as ApiErrorPayload;
    const message = errorContainer.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface SubstituteEmployee {
  id: string;
  name: string;
  occupiedDaysCount: number | null;
  occupiedRanges: DateRange[] | null;
}

interface RawDateRange {
  start_date?: string | null;
  end_date?: string | null;
}

interface RawSubstituteUser {
  id: number | string;
  name: string;
  occupied_days_count?: number | null;
  occupied_ranges?: RawDateRange[] | null;
}

function isRawSubstituteUser(value: unknown): value is RawSubstituteUser {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { id?: unknown; name?: unknown };
  const hasValidId = typeof candidate.id === 'number' || typeof candidate.id === 'string';
  return hasValidId && typeof candidate.name === 'string';
}

function mapDateRange(raw: RawDateRange): DateRange | null {
  if (!raw.start_date || !raw.end_date) {
    return null;
  }
  return {
    startDate: raw.start_date,
    endDate: raw.end_date,
  };
}

function mapSubstituteEmployee(raw: RawSubstituteUser): SubstituteEmployee {
  const occupiedRanges =
    raw.occupied_ranges?.map(mapDateRange).filter((range): range is DateRange => range !== null) ??
    null;

  return {
    id: String(raw.id),
    name: raw.name,
    occupiedDaysCount: typeof raw.occupied_days_count === 'number' ? raw.occupied_days_count : null,
    occupiedRanges: occupiedRanges && occupiedRanges.length > 0 ? occupiedRanges : null,
  };
}

interface FetchSubstituteEmployeesParams {
  startDate: string;
  endDate: string;
}

async function fetchSubstituteEmployees({
  startDate,
  endDate,
}: FetchSubstituteEmployeesParams): Promise<SubstituteEmployee[]> {
  const params = new URLSearchParams();
  params.set('start_date', startDate);
  params.set('end_date', endDate);

  const response = await apiFetch(`/api/v1/users/substitutes?${params.toString()}`);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to fetch substitute employees: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(isRawSubstituteUser).map(mapSubstituteEmployee);
}

interface UseSubstituteEmployeesOptions {
  startDate: string | null;
  endDate: string | null;
  enabled?: boolean;
}

export function useSubstituteEmployees({
  startDate,
  endDate,
  enabled = true,
}: UseSubstituteEmployeesOptions) {
  const hasValidDates = Boolean(startDate && endDate);

  return useQuery({
    queryKey: queryKeys.substituteEmployees(startDate ?? '', endDate ?? ''),
    queryFn: () =>
      fetchSubstituteEmployees({
        startDate: startDate!,
        endDate: endDate!,
      }),
    enabled: enabled && hasValidDates,
    staleTime: 1000 * 60 * 5,
  });
}
